import { fabric } from 'fabric';
import { createSegmenters, graphemes, lineUnits } from './segment';
import { getWords } from './dictionary';

export const segmenters = createSegmenters();

/*
 * Fabric splits text into "graphemes" by UTF-16 code points only, so Thai vowels and tone marks
 * become separate characters and float away once letter spacing is applied. Replace the splitter
 * with a real grapheme segmenter so "กี่" stays one unit. Every Fabric text path uses this function.
 */
if (segmenters) {
  const g = segmenters.grapheme;
  (fabric.util.string as { graphemeSplit: (s: string) => string[] }).graphemeSplit = (s: string) => graphemes(s, g);
}

export interface ThaiTextbox extends fabric.Textbox {
  wrapMode: 'thai' | 'grapheme' | 'fabric';
}

type Internals = {
  _getWidthOfCharSpacing(): number;
  _measureWord(word: string[], lineIndex: number, charOffset: number): number;
  dynamicMinWidth: number;
  callSuper(name: string, ...args: unknown[]): unknown;
};

const ThaiTextboxClass = (fabric.util as unknown as { createClass: (parent: unknown, props: object) => unknown }).createClass(fabric.Textbox, {
  type: 'thaiTextbox',
  wrapMode: 'thai',

  initialize(this: Internals, text: string, options?: object) {
    this.callSuper('initialize', text, { splitByGrapheme: true, ...options });
  },

  toObject(this: Internals, props?: string[]) {
    return this.callSuper('toObject', ['wrapMode', ...(props ?? [])]);
  },

  _wrapLine(this: Internals & ThaiTextbox, line: string, lineIndex: number, desiredWidth: number, reservedSpace = 0) {
    if (this.wrapMode !== 'thai' || !segmenters) return this.callSuper('_wrapLine', line, lineIndex, desiredWidth, reservedSpace);

    desiredWidth -= reservedSpace;
    const add = this._getWidthOfCharSpacing();
    const split = fabric.util.string.graphemeSplit;
    const lines: string[][] = [];
    let cur: string[] = [];
    let lw = 0, offset = 0, largest = 0;

    for (const u of lineUnits(line, getWords(), segmenters.word)) {
      const g = split(u.s);
      const w = this._measureWord(g, lineIndex, offset);
      if (u.k === 'space' || u.k === 'brk') { cur = cur.concat(g); lw += w; offset += g.length; continue; } // hang at line end
      if (cur.length && lw + w - add > desiredWidth) { lines.push(cur); cur = []; lw = 0; }
      if (w - add > desiredWidth) {
        // one word wider than the box: fall back to breaking between characters
        for (let k = 0; k < g.length; k++) {
          const cw = this._measureWord([g[k]], lineIndex, offset + k);
          if (cur.length && lw + cw - add > desiredWidth) { lines.push(cur); cur = []; lw = 0; }
          cur.push(g[k]); lw += cw; largest = Math.max(largest, cw);
        }
      } else { cur = cur.concat(g); lw += w; largest = Math.max(largest, w); }
      offset += g.length;
    }
    lines.push(cur);
    if (largest + reservedSpace > this.dynamicMinWidth) this.dynamicMinWidth = largest - add + reservedSpace;
    return lines;
  }
}) as new (text: string, options?: fabric.ITextboxOptions & { wrapMode?: string }) => ThaiTextbox;

/* register so loadFromJSON / clone can rebuild the class from type 'thaiTextbox' */
const reg = fabric as unknown as Record<string, unknown> & {
  Object: { _fromObject(klass: string, obj: object, cb?: (o: fabric.Object) => void, extra?: string): void };
  util: { stylesFromArray(styles: unknown, text: string): unknown };
};
reg.ThaiTextbox = Object.assign(ThaiTextboxClass, {
  fromObject(o: { styles?: unknown; text: string }, cb?: (obj: fabric.Object) => void) {
    return reg.Object._fromObject('ThaiTextbox', { ...o, styles: reg.util.stylesFromArray(o.styles, o.text) }, cb, 'text');
  }
});

export const ThaiTextbox = ThaiTextboxClass;
export const isThaiText = (o: fabric.Object | null | undefined): o is ThaiTextbox => !!o && o.type === 'thaiTextbox';
